// Snapshot payload for `customer:{id}:bid-positions:snapshot` — the per-customer
// channel introduced in BL-1306. Carries the buyer's own position on every lot
// they are engaged with, so the SnapshotContext can replace state wholesale.
//
// DELIVERY POSTURE — this payload NEVER rides the broadcast channel.
//
// The topic is hint-only: the broadcast carries `{topicId, version}` and nothing
// else, and this shape is returned exclusively by the AUTHENTICATED catch-up
// route (`/api/realtime/customer_bid_positions_snapshot/catchup`), which 403s any
// `topicId` that isn't the session's own customer. That is mandatory, not
// stylistic — `realtime.messages`' SELECT policy is a broad `v2:%` prefix match
// with no per-subscriber scoping, and the high bidder's `customers.id` is itself
// published on the PUBLIC lot snapshot as `currentBidderCustomerId`. A
// payload-bearing per-customer topic would hand any anon-key holder a one-step
// read of a competitor's hidden maximum. Contrast `user-registrations-snapshot.ts`,
// whose contents are public-safe.
//
// INVARIANT — no field here may derive from LOT state. (Load-bearing.)
//
// Everything below is a fact about the CUSTOMER: which lots they hold a live bid
// or an active max on, and the max amount. There is deliberately no `winning`,
// `outbid`, `currentPrice`, or high-bidder id. Winning/outbid is derived
// CLIENT-side from the lot snapshot's `currentBidderCustomerId` alone, so every
// input to that derivation has exactly one source.
//
// Adding a lot-derived field here would recreate the Rule #32 derived-value
// violation this topic exists to remove: the retired `/api/bids/my-status`
// computed `outbidLotIds` server-side from the realtime-projected
// `accepted_bid_customer_id_denormalized` and shipped it over REST, which
// `LotCollection` then mixed with the live `isHighBidder` — a value computed from
// two sources that drift ("moment of fetch" vs "right now").

// The two payload SHAPES are db-owned (ADR 006 + the ADR 027 untyped-JSONB
// gate): the generated Drizzle schema stamps `BidPositionsSnapshot` onto
// `bid_positions_snapshot.items`, and `packages/db` can't import from core. They
// are re-exported here so every consumer keeps importing them from
// `@auctogon/core/realtime` unchanged.
export type { BidPositionItem, BidPositionsSnapshot } from '@auctogon/db/types/jsonb';
import type Decimal from 'decimal.js';

import type { BidPositionsSnapshot } from '@auctogon/db/types/jsonb';

import { encodeRowMoney } from './money-wire-amount';

/**
 * Pre-catch-up default. A customer with no positions legitimately has NO
 * snapshot row (the migration's backfill skips them), so the catch-up route
 * synthesizes this rather than persisting a permanent empty row for every
 * customer who has ever logged in.
 */
export const EMPTY_BID_POSITIONS_SNAPSHOT: BidPositionsSnapshot = { positions: [] };

/**
 * Input row shape — duck-typed to match the Drizzle select the publisher runs.
 * Listing only the fields read here keeps callers free to project wider rows.
 *
 * `maxAmount` is the stored `max_bids.max_amount` (a Decimal, via the
 * `moneyNumeric` column type) paired with that same row's `currency`, or null
 * for a bid-only position (BL-2070).
 */
export interface BidPositionRow {
    lotId: string;
    auctionId: string;
    maxAmount: { amount: Decimal; currency: string } | null;
    hasBid: boolean;
}

/**
 * Map raw position rows to the snapshot shape.
 *
 * Encodes each max with the shared `encodeRowMoney` — the one encoder every
 * realtime money payload uses (BL-2061) — so the stored snapshot, the catch-up
 * and the client all carry `{ amount: '2500.0000', currency: 'USD' }`, and a
 * JPY max stays JPY (ADR 060 — Money Type § 2.3). A null max stays null: "no
 * standing max" is not a zero max.
 *
 * Rows are returned in the caller's order; the publisher sorts by lotId so the
 * serialized payload is stable and version bumps reflect real changes rather
 * than row-order churn.
 *
 * @param rows - The merged position rows.
 * @returns The snapshot payload.
 * @throws Error when a max's currency is not supported — the publisher's catch
 *   logs it and writes nothing, rather than storing a value no client can decode.
 */
export function mapBidPositionsSnapshot(
    rows: BidPositionRow[],
): BidPositionsSnapshot {
    return {
        positions: rows.map((row) => ({
            lotId: row.lotId,
            auctionId: row.auctionId,
            maxAmount: row.maxAmount == null ? null : encodeRowMoney(row.maxAmount),
            hasBid: row.hasBid,
        })),
    };
}
